import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/apiConfig';

/**
 * Global UI translation switch.
 *
 * Wraps every page so any component can:
 *   const { translateOn, target, setTranslateOn, translate } = useTranslation();
 *
 * Persists the toggle + target language in localStorage. Calls `/api/translate`
 * for the actual translation. The Orb voice flow does NOT use this — voice
 * commands stay in the user's primary language.
 */

const TranslationContext = createContext({
  translateOn: false, target: 'en',
  setTranslateOn: () => {}, setTarget: () => {},
  translate: async () => '',
  active: false,
});

export const TranslationProvider = ({ children }) => {
  const [translateOn, setTranslateOnRaw] = useState(false);
  const [target, setTargetRaw] = useState('en');
  const [active, setActive] = useState(false);

  useEffect(() => {
    setTranslateOnRaw(localStorage.getItem('ui_translate_on') === '1');
    setTargetRaw(localStorage.getItem('ui_translate_target') || 'en');
  }, []);

  // Probe DeepL availability once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/translate/status`);
        if (!cancelled) setActive(!!r.data?.active);
      } catch (_) { if (!cancelled) setActive(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const setTranslateOn = (v) => {
    setTranslateOnRaw(!!v);
    localStorage.setItem('ui_translate_on', v ? '1' : '0');
  };
  const setTarget = (v) => {
    setTargetRaw(v);
    localStorage.setItem('ui_translate_target', v);
  };

  const translate = useCallback(async (text, opts = {}) => {
    if (!translateOn || !text || !active) return text;
    try {
      const r = await axios.post(`${API}/translate`, {
        text, target_lang: opts.target || target,
        source_lang: opts.source || undefined,
      });
      if (r.data?.ok) return r.data.translated || text;
    } catch (_) {}
    return text;
  }, [translateOn, target, active]);

  return (
    <TranslationContext.Provider
      value={{ translateOn, target, setTranslateOn, setTarget, translate, active }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslateUI = () => useContext(TranslationContext);
